(function () {
  'use strict';

  /* ============================================================
     RSI LEAD FORM — photo upload + compression + submission

     Shared by the "Sell My Pallet Racking" and "Pallet Racking
     Installation" enquiry forms (assets/rsi-we-buy-racking.js used to
     own this logic for the first page alone; extracted here so both
     pages get the same fix instead of maintaining two copies).

     WHY COMPRESSION: a real phone photo is routinely 3-12MB (more for
     HEIC-shot iPhones saved as high-res JPEG). Several of those attached
     to one submission easily built a 30-50MB+ multipart payload, which
     is why uploads were failing — that's larger than the request-body
     limit on essentially any webhook consumer (Zapier/Make/n8n/a
     serverless function all commonly cap well under that). Every image
     is now downscaled and re-encoded client-side before it's ever added
     to the form, which typically brings a multi-MB photo down to a few
     hundred KB, regardless of what the original size was — "accept any
     size" is handled by shrinking the file, not by raising a limit
     somewhere that will only need raising again for the next larger
     photo.

     IMPORTANT — this still requires a real webhook_url configured in the
     section's theme-editor settings to actually deliver anywhere: this
     compresses and posts the images from the page, but Shopify's own
     native /contact endpoint silently discards file attachments no
     matter how small they are — it only ever accepts plain form fields.
     Until a webhook is set, the honest WhatsApp/email fallback note
     (already wired up per-form below) is what actually reaches the
     business for photos.
  ============================================================ */

  var MAX_DIMENSION = 1600; // px, longest side after compression
  var JPEG_QUALITY = 0.75;
  var SKIP_COMPRESSION_UNDER = 400 * 1024; // bytes — already-small files are sent as-is
  var MAX_ORIGINAL_FILE_SIZE = 30 * 1024 * 1024; // 30MB — a sanity ceiling, not the normal case
  var MAX_FILES = 8;

  /**
   * Compresses an image file client-side via canvas re-encoding. Resolves
   * with the ORIGINAL file (unchanged) for non-images, already-small
   * files, or anything the browser can't decode as an image (e.g. HEIC
   * in a browser with no native HEIC support) — never blocks the
   * upload, only shrinks it when it safely can.
   * @param {File} file
   * @returns {Promise<File>}
   */
  function compressImage(file) {
    return new Promise(function (resolve) {
      if (!file.type || file.type.indexOf('image/') !== 0 || file.size <= SKIP_COMPRESSION_UNDER) {
        resolve(file);
        return;
      }

      var objectUrl = URL.createObjectURL(file);
      var img = new Image();

      img.onload = function () {
        URL.revokeObjectURL(objectUrl);

        var scale = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
        var width = Math.max(1, Math.round(img.naturalWidth * scale));
        var height = Math.max(1, Math.round(img.naturalHeight * scale));

        var canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        var ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(file);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          function (blob) {
            if (!blob || blob.size >= file.size) {
              // Compression didn't help (rare, e.g. a already-efficient
              // small JPEG upscaled from a tiny source) — keep the original.
              resolve(file);
              return;
            }
            var newName = file.name.replace(/\.(heic|heif|png|webp)$/i, '.jpg');
            if (newName === file.name && !/\.jpe?g$/i.test(newName)) {
              newName += '.jpg';
            }
            resolve(new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() }));
          },
          'image/jpeg',
          JPEG_QUALITY
        );
      };

      img.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      };

      img.src = objectUrl;
    });
  }

  // ---- Media uploader (dropzone + previews + compression) ----
  document.querySelectorAll('[data-rsi-upload]').forEach(function (uploadRoot) {
    var input = uploadRoot.querySelector('input[type="file"]');
    var preview = uploadRoot.querySelector('[data-rsi-upload-preview]');
    if (!input || !preview) return;

    var selectedFiles = [];
    var objectUrls = [];
    var isProcessing = false;

    uploadRoot.addEventListener('dragover', function (e) {
      e.preventDefault();
      uploadRoot.classList.add('is-drag-over');
    });

    uploadRoot.addEventListener('dragleave', function (e) {
      if (!uploadRoot.contains(e.relatedTarget)) {
        uploadRoot.classList.remove('is-drag-over');
      }
    });

    uploadRoot.addEventListener('drop', function (e) {
      e.preventDefault();
      uploadRoot.classList.remove('is-drag-over');
      if (e.dataTransfer && e.dataTransfer.files.length) {
        addFiles(e.dataTransfer.files);
      }
    });

    input.addEventListener('change', function () {
      addFiles(input.files);
    });

    function addFiles(files) {
      var incoming = Array.from(files || []);
      if (!incoming.length) return;

      var room = MAX_FILES - selectedFiles.length;
      if (room <= 0) {
        setNotice('Up to ' + MAX_FILES + ' files — remove one to add another.');
        syncInputFiles();
        return;
      }

      var tooLarge = [];
      var accepted = [];
      incoming.slice(0, room).forEach(function (file) {
        if (file.size > MAX_ORIGINAL_FILE_SIZE) {
          tooLarge.push(file.name);
        } else {
          accepted.push(file);
        }
      });

      if (incoming.length > room) {
        setNotice('Only ' + room + ' more file' + (room === 1 ? '' : 's') + ' can be added (max ' + MAX_FILES + ').');
      } else if (tooLarge.length) {
        setNotice(tooLarge.join(', ') + ' — over the 30MB file limit, not added.');
      } else {
        setNotice('');
      }

      if (!accepted.length) {
        syncInputFiles();
        return;
      }

      isProcessing = true;
      renderPreview();

      var existingKeys = {};
      selectedFiles.forEach(function (file) {
        existingKeys[fileKey(file)] = true;
      });

      Promise.all(accepted.map(compressImage)).then(function (compressed) {
        compressed.forEach(function (file, i) {
          var key = fileKey(accepted[i]);
          if (!existingKeys[key]) {
            selectedFiles.push(file);
            existingKeys[key] = true;
          }
        });
        isProcessing = false;
        syncInputFiles();
        renderPreview();
      });
    }

    function syncInputFiles() {
      if (typeof DataTransfer === 'undefined') return;

      var dataTransfer = new DataTransfer();
      selectedFiles.forEach(function (file) {
        dataTransfer.items.add(file);
      });

      input.files = dataTransfer.files;
    }

    function setNotice(message) {
      var noticeEl = uploadRoot.querySelector('[data-rsi-upload-notice]');
      if (!noticeEl) return;
      noticeEl.textContent = message;
      noticeEl.hidden = !message;
    }

    function renderPreview() {
      revokeObjectUrls();
      preview.innerHTML = '';

      selectedFiles.forEach(function (file, index) {
        var card = document.createElement('div');
        card.className = 'rsi-upload-preview__item';

        if (isPreviewableImage(file)) {
          var img = document.createElement('img');
          var objectUrl = URL.createObjectURL(file);
          objectUrls.push(objectUrl);
          img.src = objectUrl;
          img.alt = file.name;
          card.appendChild(img);
        } else {
          var fileIcon = document.createElement('div');
          fileIcon.className = 'rsi-upload-preview__file';
          fileIcon.textContent = fileLabel(file);
          card.appendChild(fileIcon);
        }

        var body = document.createElement('div');
        body.className = 'rsi-upload-preview__body';

        var name = document.createElement('span');
        name.className = 'rsi-upload-preview__name';
        name.textContent = file.name;

        var size = document.createElement('span');
        size.className = 'rsi-upload-preview__size';
        size.textContent = formatFileSize(file.size);

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'rsi-upload-preview__remove';
        remove.textContent = 'Remove';
        remove.addEventListener('click', function () {
          selectedFiles.splice(index, 1);
          syncInputFiles();
          renderPreview();
        });

        body.appendChild(name);
        body.appendChild(size);
        card.appendChild(body);
        card.appendChild(remove);
        preview.appendChild(card);
      });

      if (isProcessing) {
        var processingCard = document.createElement('div');
        processingCard.className = 'rsi-upload-preview__item rsi-upload-preview__item--processing';
        processingCard.textContent = 'Compressing…';
        preview.appendChild(processingCard);
      }

      uploadRoot.classList.toggle('has-files', selectedFiles.length > 0 || isProcessing);
    }

    function revokeObjectUrls() {
      objectUrls.forEach(function (url) {
        URL.revokeObjectURL(url);
      });
      objectUrls = [];
    }

    function fileKey(file) {
      return [file.name, file.size, file.lastModified, file.type].join('|');
    }

    function fileLabel(file) {
      var parts = file.name.split('.');
      var extension = parts.length > 1 ? parts.pop().toUpperCase() : 'FILE';
      return extension.length <= 4 ? extension : 'FILE';
    }

    function isPreviewableImage(file) {
      if (!file.type || file.type.indexOf('image/') !== 0) return false;
      return !/\.(heic|heif)$/i.test(file.name);
    }

    function formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return '';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
      return (bytes / 1048576).toFixed(1) + ' MB';
    }
  });

  // ---- Form handlers (webhook AJAX submit, or native Shopify contact fallback) ----
  var shopifySuccess = new URLSearchParams(window.location.search).get('contact[success]') === 'true';

  document.querySelectorAll('[data-rsi-we-buy-form], [data-rsi-lead-form]').forEach(function (form) {
    var webhookUrl    = (form.getAttribute('data-rsi-webhook')  || '').trim();
    var fallbackUrl    = (form.getAttribute('data-rsi-whatsapp') || form.getAttribute('data-rsi-fallback-link') || '').trim();
    var fallbackLabel  = form.getAttribute('data-rsi-fallback-label') || 'send your photos via WhatsApp';
    var contactErrMsg = form.getAttribute('data-rsi-contact-error') || 'Please add a phone number or email address so we can contact you.';
    var elSuccess     = document.getElementById(form.getAttribute('data-rsi-success')  || '');
    var elError       = document.getElementById(form.getAttribute('data-rsi-error')    || '');
    var elFallback    = document.getElementById(form.getAttribute('data-rsi-fallback') || '');
    var elUploadWrap  = document.getElementById(form.getAttribute('data-rsi-upload-wrap') || '');
    var elSubmit      = form.querySelector('[data-rsi-submit]');
    var origBtnText   = elSubmit ? elSubmit.textContent : '';

    // The upload field only actually sends anywhere when a webhook is
    // configured (Shopify's native /contact endpoint silently drops file
    // attachments). Show the uploader only when it will really work;
    // otherwise show the honest WhatsApp/email fallback note instead —
    // never both, so the form never contradicts itself.
    if (webhookUrl) {
      if (elUploadWrap) elUploadWrap.style.display = '';
    } else {
      if (elFallback) elFallback.style.display = 'block';
    }

    // Handle Shopify contact redirect (contact[success]=true in URL)
    if (shopifySuccess) {
      form.style.display = 'none';
      if (elSuccess) {
        elSuccess.innerHTML = fallbackUrl
          ? 'Your details have been received. Please <a href="' + fallbackUrl + '" target="_blank" rel="noopener noreferrer">' + fallbackLabel + '</a> so we can prepare your quote.'
          : 'Your details have been received. We\'ll be in touch shortly.';
        elSuccess.style.display = 'block';
      }
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      // Consent checkbox (where present)
      var consent = form.querySelector('[name="consent"]');
      if (consent && !consent.checked) {
        showErr('Please confirm your consent before submitting.');
        return;
      }

      // At least one of phone or email is required, even though neither
      // field is individually marked required.
      var phoneField = form.querySelector('[name="contact[phone]"]');
      var emailField = form.querySelector('[name="contact[email]"]');
      var phoneVal = phoneField ? phoneField.value.trim() : '';
      var emailVal = emailField ? emailField.value.trim() : '';
      if (!phoneVal && !emailVal) {
        showErr(contactErrMsg);
        if (phoneField) phoneField.focus();
        return;
      }

      hideErr();

      if (!webhookUrl) {
        // No webhook — native Shopify contact POST
        // form.submit() does not fire the submit event, so no infinite loop
        form.submit();
        return;
      }

      // Webhook configured — AJAX FormData POST (includes files)
      if (elSubmit) { elSubmit.disabled = true; elSubmit.textContent = 'Sending…'; }

      fetch(webhookUrl, { method: 'POST', body: new FormData(form) })
        .then(function (res) {
          if (res.ok) {
            form.style.display = 'none';
            if (elSuccess) {
              elSuccess.innerHTML = 'Your details and photos have been received. We\'ll be in touch with your quote soon.';
              elSuccess.style.display = 'block';
            }
          } else {
            throw new Error('HTTP ' + res.status);
          }
        })
        .catch(function (err) {
          console.warn('[RSI Lead Form] Submission error:', err);
          showErr(fallbackUrl
            ? 'Something went wrong. Please <a href="' + fallbackUrl + '" target="_blank" rel="noopener noreferrer">' + fallbackLabel + '</a> or call 01827 213073.'
            : 'Something went wrong. Please call 01827 213073 or email sales@rackitstackit.co.uk.');
          if (elSubmit) { elSubmit.disabled = false; elSubmit.textContent = origBtnText; }
        });
    });

    function showErr(msg) {
      if (!elError) return;
      elError.innerHTML = msg;
      elError.style.display = 'block';
      elError.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function hideErr() {
      if (elError) elError.style.display = 'none';
    }
  });
})();
